import React from 'react';

import PropTypes from 'prop-types';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { PackageIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import PackagesActivityCard from './components/PackagesActivityCard';
import PackagesCustomDataView from './components/PackagesCustomDataView';
import PackagesEntityBlock from './components/PackagesEntityBlock';
import PackagesEntityMenuItem from './components/PackagesEntityMenuItem';
import PackagesMissingEntities from './components/PackagesMissingEntities';
import PackagesPickerEmptyState from './components/PackagesPickerEmptyState';
import PackagesPickerError from './components/PackagesPickerError';
import PackagesPickerNoSearchFilterResults from './components/PackagesPickerNoSearchFilterResults';
import PackagesPluginProvider from './components/PackagesPluginProvider';
import PackagesSearchFilterButton from './components/PackagesSearchFilterButton';
import PackagesSearchFilterPanel from './components/PackagesSearchFilterPanel';
import { PACKAGE_LIST_LIMIT } from './helpers/consts';
import formatFilterParams from './helpers/formatFilterParams';
import Services from './helpers/sdkServices';

const PickerModalTitle = ({ showTabTitle = true }) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const value = T.translate('ADD_REFERENCE');

  return (
    <>
      {showTabTitle && <TabTitle value={value} />}
      {value}
    </>
  );
};

PickerModalTitle.propTypes = {
  showTabTitle: PropTypes.bool,
};

export default {
  metadata: {
    domain: 'autodesk-construction-filepackages',
    type: 'package',
  },
  is3LOSupported: true,
  renderIcon: (p) => <PackageIcon {...p} />,
  toolId: TOOL_IDS.DOCUMENTS,
  // Avoid ignoring `searchExpression` when clear filters
  showSearchBox: false,
  modalProps: {
    width: 1140,
    height: 1000,
  },
  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('TITLE');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle,
  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ADD_BUTTON', { context: count });
  },
  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('SELECTED_COUNT', { context: count });
  },
  queryEntities: async ({ projectId, offset, filters }) => {
    const { searchExpression, ...panelFilters } = filters;
    const { results, pagination } = await Services.PackagesList.getPackages(
      projectId,
      {
        offset,
        limit: PACKAGE_LIST_LIMIT,
        ...(searchExpression ? { searchText: searchExpression } : null),
        filter: formatFilterParams(panelFilters),
      },
    );
    const nextOffset = pagination.offset + pagination.limit;

    return {
      data: results,
      ...(nextOffset < pagination.totalResults && {
        nextPageQuery: { projectId, offset: nextOffset, filters },
      }),
    };
  },
  getEntitiesByIds: async ({ ids: packageIds, projectId }) => {
    const { results } = await Services.PackagesList.batchGetPackages(
      projectId,
      packageIds.map((packageId) => ({ id: packageId })),
    );
    return { data: results };
  },
  FiltersPanel: PackagesSearchFilterPanel,
  CustomDataView: PackagesCustomDataView,
  CustomFilterButton: PackagesSearchFilterButton,
  CustomPickerError: PackagesPickerError,
  CustomPickerEmptyState: PackagesPickerEmptyState,
  CustomPickerNoSearchResults: PackagesPickerNoSearchFilterResults,
  CustomPickerSearchBox: () => null,
  EntityBlock: PackagesEntityBlock,
  CustomMissingEntities: PackagesMissingEntities,
  EntityMenuItem: PackagesEntityMenuItem,
  ActivityCard: PackagesActivityCard,
  Provider: PackagesPluginProvider,
};
